/** UI strings for the F09 model (hydrogen states); merged into the photons dictionary. */
export const sv = {
  hsTitle: "Väteatomens tillstånd: täthet, radiell fördelning och mätningar",
  hsNote:
    "Stationära tillstånd ψ för en elektron och en kärna med laddningen Ze, med vätets reducerade massa (a = 52,9465 pm, |E₁| = 13,598 eV). Längder anges i a. Mätningarna av r dras med ett fast frö ur P(r); samma frö och samma N ger alltid samma mätserie.",
  hsViewRadial: "Radiell fördelning",
  hsViewMeasure: "Täthet och mätningar",
  hsViewSlice: "Snitt och kvanttal",
  hsN: "Huvudkvanttal n",
  hsL: "Bankvanttal ℓ",
  hsM: "Magnetiskt kvanttal m_ℓ",
  hsCharge: "Kärnladdning Z (vätets m_r)",
  hsRadius: "Radie R (a)",
  hsReference: "Jämförelseradie r₀ (a)",
  hsWidth: "Skaltjocklek Δr (a)",
  hsSamples: "Antal mätningar N",
  hsReseed: "Nya mätningar",
  hsHistogram: "Histogram",
  hsPerShell: "Per radieintervall",
  hsPerVolume: "Per volym",
  hsCompare: "Jämförelse",
  hsCompareNone: "Ingen",
  hsCompareSecond: "Ett andra tillstånd",
  hsCompareN: "n för jämförelsen",
  hsCompareL: "ℓ för jämförelsen",
  hsAxis: "Radiell axel",
  hsAxisA: "r/a",
  hsAxisScaled: "r/(n²a/Z)",
  hsAngular: "Vinkeldel",
  hsSingle: "Ett m_ℓ",
  hsSummed: "Summa över m_ℓ",
  hsRadialSvg: "Radiell sannolikhetstäthet",
  hsRadialDesc:
    "P(r) = r²|R|² mot r för det valda tillståndet och eventuellt ett jämförelsetillstånd (streckat). Ytan till höger om radien R är skuggad. Lodräta linjer markerar R, typvärdet, väntevärdet och det klassiska yttre vändavståndet; små streck på axeln markerar radiella noder.",
  hsMeasureSvg: "Täthet per volym, radiell täthet och mätningar",
  hsMeasureDesc:
    "Vinkelmedelvärdet av |ψ|² och P(r), var för sig normerade till sitt största värde, mot r. Staplarna är ett histogram över N mätningar av r, antingen per radieintervall (jämförs med P) eller per volym (jämförs med |ψ|²). Lodräta linjer markerar R och r₀.",
  hsSliceSvg: "Snitt genom |ψ|²",
  hsSliceDesc:
    "|ψ|² i xz-planet med z-axeln uppåt och kärnan i mitten, i kvadratrotsskala relativt tillståndets största värde. Eftersom |ψ|² inte beror på φ ser alla plan genom z-axeln likadana ut.",
  hsAxisR: "r ({unit})",
  hsAxisP: "P(r)",
  hsAxisRelative: "relativ täthet",
  hsLegendVolume: "|ψ|², per volym",
  hsLegendRadial: "P(r), per radieintervall",
  hsMarkProbe: "R",
  hsMarkReference: "r₀",
  hsMarkMode: "typvärde",
  hsMarkMean: "⟨r⟩",
  hsMarkTurning: "r₊",
  hsSliceAxis: "x och z från −{half} a till {half} a",
  hsState: "Tillstånd",
  hsEnergy: "E_n = −13,598 eV · Z²/n² (eV)",
  hsRadialNodes: "Radiella noder, n − ℓ − 1",
  hsNodePositions: "Nodernas radier (a)",
  hsMode: "Typvärde för P(r) (a)",
  hsMean: "Väntevärde ⟨r⟩ (a)",
  hsSpread: "Standardavvikelse σ_r (a)",
  hsRelative: "σ_r/⟨r⟩",
  hsInside: "Pr(r < R)",
  hsOutside: "Pr(r > R)",
  hsTurning: "Klassiskt yttre vändavstånd r₊ (a)",
  hsBeyond: "Pr(r > r₊), klassiskt förbjudet",
  hsRadiusPm: "R (pm)",
  hsCompareState: "Jämförelsetillstånd",
  hsCompareMode: "Typvärde, jämförelse (a)",
  hsCompareMean: "⟨r⟩, jämförelse (a)",
  hsCompareRelative: "σ_r/⟨r⟩, jämförelse",
  hsWidthRatio: "Kvot σ_r/⟨r⟩, jämförelse mot valt",
  hsDensityRatio: "|ψ|²(R)/|ψ|²(r₀), lika små volymer",
  hsShellRatio: "P(R)/P(r₀), lika tunna skal",
  hsShell: "Pr(R ≤ r ≤ R + Δr)",
  hsDensityAt: "Vinkelmedelvärde av |ψ|² vid R (a⁻³)",
  hsSampleCount: "Mätningar N",
  hsSampleInside: "Andel mätningar med r < R",
  hsSampleMean: "Medelvärde av mätningarna (a)",
  hsDegeneracy: "Rumsliga tillstånd med samma n (n²)",
  hsSubshells: "Fördelning på ℓ (2ℓ + 1 var)",
  hsLength: "|L|/ħ = √(ℓ(ℓ + 1))",
  hsLz: "L_z/ħ = m_ℓ",
  hsAngle: "Vinkel mellan L och z-axeln (°)",
  hsAngularNodes: "Vinkelnoder i |ψ|², ℓ − |m_ℓ|",
  hsAngularShape: "4π|Y|²/(2ℓ + 1) vid θ = 0°, 45°, 90°",
  hsRadialFormula: String.raw`$P(r)\,dr=r^2|R_{n\ell}|^2\,dr$ (41.25) är sannolikheten att mäta avståndet i skalet mellan $r$ och $r+dr$. $\langle r\rangle=\dfrac{a}{2Z}\left[3n^2-\ell(\ell+1)\right]$, $\langle r^2\rangle=\dfrac{a^2n^2}{2Z^2}\left[5n^2+1-3\ell(\ell+1)\right]$; vändavståndet $r_+=\dfrac{n^2a}{Z}\left(1+\sqrt{1-\ell(\ell+1)/n^2}\right)$ ($2n^2a/Z$ för $\ell=0$, där $U=E_n$).`,
  hsMeasureFormula: String.raw`Samma tillstånd, två mått: $|\psi|^2$ är sannolikhet per volym, $P(r)=4\pi r^2\,\overline{|\psi|^2}$ är sannolikhet per radieintervall. För 1s är $|\psi|^2$ störst vid kärnan men $P$ störst vid $r=a$. Ett histogram per volym delar antalet mätningar med skalets volym $\tfrac{4\pi}{3}(r_2^3-r_1^3)$.`,
  hsSliceFormula: String.raw`$\psi_{n\ell m_\ell}=R_{n\ell}(r)\,\Theta_{\ell m_\ell}(\theta)\,e^{im_\ell\phi}/\sqrt{2\pi}$, så $|\psi|^2$ beror inte på $\phi$. $|L|=\sqrt{\ell(\ell+1)}\,\hbar$ (41.22), $L_z=m_\ell\hbar$ (41.23), $E_n=-13{,}598\,\mathrm{eV}\cdot Z^2/n^2$ (41.21) oberoende av $\ell$ och $m_\ell$. Summan över $m_\ell$ är $(2\ell+1)/(4\pi)\cdot|R_{n\ell}|^2$, sfäriskt symmetrisk.`,
};

export const en: Record<keyof typeof sv, string> = {
  hsTitle: "Hydrogen states: density, radial distribution and measurements",
  hsNote:
    "Stationary states ψ of one electron and a nucleus of charge Ze, with hydrogen's reduced mass (a = 52.9465 pm, |E₁| = 13.598 eV). Lengths are in units of a. The measurements of r are drawn from P(r) with a fixed seed; the same seed and N always give the same series.",
  hsViewRadial: "Radial distribution",
  hsViewMeasure: "Density and measurements",
  hsViewSlice: "Slice and quantum numbers",
  hsN: "Principal quantum number n",
  hsL: "Orbital quantum number ℓ",
  hsM: "Magnetic quantum number m_ℓ",
  hsCharge: "Nuclear charge Z (hydrogen's m_r)",
  hsRadius: "Radius R (a)",
  hsReference: "Reference radius r₀ (a)",
  hsWidth: "Shell thickness Δr (a)",
  hsSamples: "Number of measurements N",
  hsReseed: "New measurements",
  hsHistogram: "Histogram",
  hsPerShell: "Per radial interval",
  hsPerVolume: "Per volume",
  hsCompare: "Comparison",
  hsCompareNone: "None",
  hsCompareSecond: "A second state",
  hsCompareN: "n of the comparison",
  hsCompareL: "ℓ of the comparison",
  hsAxis: "Radial axis",
  hsAxisA: "r/a",
  hsAxisScaled: "r/(n²a/Z)",
  hsAngular: "Angular part",
  hsSingle: "One m_ℓ",
  hsSummed: "Sum over m_ℓ",
  hsRadialSvg: "Radial probability density",
  hsRadialDesc:
    "P(r) = r²|R|² against r for the chosen state and optionally a comparison state (dashed). The area to the right of the radius R is shaded. Vertical lines mark R, the most probable radius, the expectation value and the classical outer turning point; small ticks on the axis mark radial nodes.",
  hsMeasureSvg: "Density per volume, radial density and measurements",
  hsMeasureDesc:
    "The angle average of |ψ|² and P(r), each normalized to its own maximum, against r. The bars are a histogram of N measurements of r, either per radial interval (compare with P) or per volume (compare with |ψ|²). Vertical lines mark R and r₀.",
  hsSliceSvg: "Slice through |ψ|²",
  hsSliceDesc:
    "|ψ|² in the xz-plane with the z-axis up and the nucleus at the centre, on a square-root scale relative to the state's maximum. Since |ψ|² does not depend on φ, every plane through the z-axis looks the same.",
  hsAxisR: "r ({unit})",
  hsAxisP: "P(r)",
  hsAxisRelative: "relative density",
  hsLegendVolume: "|ψ|², per volume",
  hsLegendRadial: "P(r), per radial interval",
  hsMarkProbe: "R",
  hsMarkReference: "r₀",
  hsMarkMode: "most probable",
  hsMarkMean: "⟨r⟩",
  hsMarkTurning: "r₊",
  hsSliceAxis: "x and z from −{half} a to {half} a",
  hsState: "State",
  hsEnergy: "E_n = −13.598 eV · Z²/n² (eV)",
  hsRadialNodes: "Radial nodes, n − ℓ − 1",
  hsNodePositions: "Node radii (a)",
  hsMode: "Most probable r of P(r) (a)",
  hsMean: "Expectation value ⟨r⟩ (a)",
  hsSpread: "Standard deviation σ_r (a)",
  hsRelative: "σ_r/⟨r⟩",
  hsInside: "Pr(r < R)",
  hsOutside: "Pr(r > R)",
  hsTurning: "Classical outer turning point r₊ (a)",
  hsBeyond: "Pr(r > r₊), classically forbidden",
  hsRadiusPm: "R (pm)",
  hsCompareState: "Comparison state",
  hsCompareMode: "Most probable r, comparison (a)",
  hsCompareMean: "⟨r⟩, comparison (a)",
  hsCompareRelative: "σ_r/⟨r⟩, comparison",
  hsWidthRatio: "Ratio of σ_r/⟨r⟩, comparison to chosen",
  hsDensityRatio: "|ψ|²(R)/|ψ|²(r₀), equal small volumes",
  hsShellRatio: "P(R)/P(r₀), equal thin shells",
  hsShell: "Pr(R ≤ r ≤ R + Δr)",
  hsDensityAt: "Angle average of |ψ|² at R (a⁻³)",
  hsSampleCount: "Measurements N",
  hsSampleInside: "Fraction of measurements with r < R",
  hsSampleMean: "Mean of the measurements (a)",
  hsDegeneracy: "Spatial states with the same n (n²)",
  hsSubshells: "Distribution over ℓ (2ℓ + 1 each)",
  hsLength: "|L|/ħ = √(ℓ(ℓ + 1))",
  hsLz: "L_z/ħ = m_ℓ",
  hsAngle: "Angle between L and the z-axis (°)",
  hsAngularNodes: "Angular nodes in |ψ|², ℓ − |m_ℓ|",
  hsAngularShape: "4π|Y|²/(2ℓ + 1) at θ = 0°, 45°, 90°",
  hsRadialFormula: String.raw`$P(r)\,dr=r^2|R_{n\ell}|^2\,dr$ (41.25) is the probability of measuring the distance in the shell between $r$ and $r+dr$. $\langle r\rangle=\dfrac{a}{2Z}\left[3n^2-\ell(\ell+1)\right]$, $\langle r^2\rangle=\dfrac{a^2n^2}{2Z^2}\left[5n^2+1-3\ell(\ell+1)\right]$; turning point $r_+=\dfrac{n^2a}{Z}\left(1+\sqrt{1-\ell(\ell+1)/n^2}\right)$ ($2n^2a/Z$ for $\ell=0$, where $U=E_n$).`,
  hsMeasureFormula: String.raw`One state, two measures: $|\psi|^2$ is probability per volume, $P(r)=4\pi r^2\,\overline{|\psi|^2}$ is probability per radial interval. For 1s, $|\psi|^2$ is largest at the nucleus but $P$ is largest at $r=a$. A histogram per volume divides the number of measurements by the shell volume $\tfrac{4\pi}{3}(r_2^3-r_1^3)$.`,
  hsSliceFormula: String.raw`$\psi_{n\ell m_\ell}=R_{n\ell}(r)\,\Theta_{\ell m_\ell}(\theta)\,e^{im_\ell\phi}/\sqrt{2\pi}$, so $|\psi|^2$ does not depend on $\phi$. $|L|=\sqrt{\ell(\ell+1)}\,\hbar$ (41.22), $L_z=m_\ell\hbar$ (41.23), $E_n=-13.598\,\mathrm{eV}\cdot Z^2/n^2$ (41.21) independent of $\ell$ and $m_\ell$. The sum over $m_\ell$ is $(2\ell+1)/(4\pi)\cdot|R_{n\ell}|^2$, spherically symmetric.`,
};
